import React, { useRef } from 'react';
import { Button, Typography, Space } from 'antd';
import { 
  CloudUploadOutlined, 
  PaperClipOutlined, 
  FileImageOutlined,
  SoundOutlined,
  FileTextOutlined 
} from '@ant-design/icons';
import styled from 'styled-components';
import { useFileUpload, UseFileUploadOptions } from '../hooks/useFileUpload';
import { useDragAndDrop } from '../hooks/useDragAndDrop';
import FilePreview from './FilePreview';

const { Text } = Typography;

// 样式组件
const UploadContainer = styled.div`
  width: 100%;
`;

const DropZone = styled.div<{ $isDragging: boolean; $disabled: boolean }>`
  position: relative;
  border: 2px dashed ${props => props.$isDragging ? '#1890ff' : '#d9d9d9'};
  border-radius: 12px;
  background: ${props => {
    if (props.$disabled) return '#f5f5f5';
    if (props.$isDragging) return 'rgba(24, 144, 255, 0.05)';
    return '#fafafa';
  }};
  padding: 24px;
  text-align: center;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${props => props.$disabled ? '#d9d9d9' : '#1890ff'};
    background: ${props => {
      if (props.$disabled) return '#f5f5f5';
      return 'rgba(24, 144, 255, 0.03)';
    }};
  }
  
  ${props => props.$isDragging && `
    transform: scale(1.02);
    box-shadow: 0 4px 16px rgba(24, 144, 255, 0.2);
  `}
`;

const UploadIcon = styled.div<{ $isDragging: boolean }>`
  font-size: 48px;
  color: ${props => props.$isDragging ? '#1890ff' : '#bfbfbf'};
  margin-bottom: 16px;
  transition: all 0.3s ease;
  
  ${props => props.$isDragging && `
    transform: scale(1.1);
    animation: bounce 1s infinite;
  `}
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0) scale(1.1);
    }
    40% {
      transform: translateY(-10px) scale(1.1);
    }
    60% {
      transform: translateY(-5px) scale(1.1);
    }
  }
`;

const UploadText = styled.div`
  margin-bottom: 8px;
`;

const UploadTitle = styled(Text)`
  font-size: 16px;
  font-weight: 500;
  color: #262626;
  display: block;
  margin-bottom: 4px;
`;

const UploadHint = styled(Text)`
  font-size: 14px;
  color: #8c8c8c;
  display: block;
`;

const SupportedTypes = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const TypeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #595959;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
`;

const HiddenInput = styled.input`
  display: none;
`;

// 组件接口
interface FileUploadAreaProps extends UseFileUploadOptions {
  disabled?: boolean;
  showPreview?: boolean;
  showSupportedTypes?: boolean;
  uploadText?: string;
  uploadHint?: string;
  dragText?: string;
  onUpload?: (files: File[]) => Promise<void>;
  className?: string;
}

/**
 * 文件上传区域组件
 */
const FileUploadArea: React.FC<FileUploadAreaProps> = ({
  disabled = false,
  showPreview = true,
  showSupportedTypes = true,
  uploadText = "点击或拖拽文件到此处上传",
  uploadHint = "支持单个或批量文件上传",
  dragText = "释放文件开始上传",
  onUpload,
  className,
  ...uploadOptions
}) => {
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  
  // 文件上传Hook
  const {
    files,
    addFiles,
    removeFile,
    clearFiles,
    getAcceptString,
    handleFileInputChange,
    isUploading,
    updateFileStatus,
    options
  } = useFileUpload({
    ...uploadOptions,
    onFileAdd: (file) => {
      uploadOptions.onFileAdd?.(file);
      // 自动上传
      if (onUpload) {
        handleUpload([file.file]);
      }
    }
  });

  // 拖拽Hook
  const { isDragging, getRootProps } = useDragAndDrop({
    onFilesDropped: addFiles,
    disabled: disabled || isUploading,
    accept: [
      ...options.supportedImageTypes,
      ...options.supportedAudioTypes,
      ...options.supportedDocumentTypes
    ],
    multiple: true
  });

  /**
   * 处理文件上传
   */
  const handleUpload = async (filesToUpload: File[]) => {
    if (!onUpload || filesToUpload.length === 0) return;

    try {
      // 更新文件状态为上传中
      filesToUpload.forEach(file => {
        const fileItem = files.find(f => f.file === file);
        if (fileItem) {
          updateFileStatus(fileItem.id, 'uploading', 0);
        }
      });

      // 执行上传
      await onUpload(filesToUpload);

      // 更新文件状态为成功
      filesToUpload.forEach(file => {
        const fileItem = files.find(f => f.file === file);
        if (fileItem) {
          updateFileStatus(fileItem.id, 'success', 100);
        }
      });

    } catch (error) {
      console.error('文件上传失败:', error);
      
      // 更新文件状态为失败
      filesToUpload.forEach(file => {
        const fileItem = files.find(f => f.file === file);
        if (fileItem) {
          updateFileStatus(fileItem.id, 'error');
        }
      });
    }
  };

  /**
   * 点击上传区域
   */
  const handleClick = () => {
    if (disabled || isUploading) return;
    hiddenInputRef.current?.click();
  };

  /**
   * 获取支持的文件类型显示
   */
  const getSupportedTypesDisplay = () => {
    const types = [
      { icon: <FileImageOutlined />, label: '图片', types: options.supportedImageTypes },
      { icon: <SoundOutlined />, label: '音频', types: options.supportedAudioTypes },
      { icon: <FileTextOutlined />, label: '文档', types: options.supportedDocumentTypes }
    ].filter(type => type.types.length > 0);

    return types.map((type, index) => (
      <TypeItem key={index}>
        {type.icon}
        <span>{type.label}</span>
      </TypeItem>
    ));
  };

  return (
    <UploadContainer className={className}>
      {/* 文件预览 */}
      {showPreview && (
        <FilePreview
          files={files}
          onRemove={removeFile}
          showProgress={true}
        />
      )}

      {/* 上传区域 */}
      <DropZone
        {...getRootProps()}
        $isDragging={isDragging}
        $disabled={disabled || isUploading}
        onClick={handleClick}
      >
        <UploadIcon $isDragging={isDragging}>
          <CloudUploadOutlined />
        </UploadIcon>

        <UploadText>
          <UploadTitle>
            {isDragging ? dragText : uploadText}
          </UploadTitle>
          <UploadHint>
            {isDragging 
              ? "释放鼠标完成上传"
              : uploadHint
            }
          </UploadHint>
        </UploadText>

        {/* 支持的文件类型 */}
        {showSupportedTypes && !isDragging && (
          <SupportedTypes>
            {getSupportedTypesDisplay()}
          </SupportedTypes>
        )}

        {/* 操作按钮 */}
        {!isDragging && (
          <ActionButtons>
            <Button
              type="primary"
              icon={<PaperClipOutlined />}
              disabled={disabled || isUploading}
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
            >
              选择文件
            </Button>
            
            {files.length > 0 && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  clearFiles();
                }}
                disabled={isUploading}
              >
                清空文件
              </Button>
            )}
          </ActionButtons>
        )}

        {/* 隐藏的文件输入 */}
        <HiddenInput
          ref={hiddenInputRef}
          type="file"
          accept={getAcceptString()}
          multiple
          onChange={handleFileInputChange}
          disabled={disabled || isUploading}
        />
      </DropZone>

      {/* 文件信息提示 */}
      {files.length > 0 && (
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            已选择 {files.length} 个文件，总大小 {
              ((files.reduce((sum, f) => sum + f.file.size, 0)) / 1024 / 1024).toFixed(1)
            } MB
          </Text>
        </div>
      )}
    </UploadContainer>
  );
};

export default FileUploadArea; 